import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { VideoCameraIcon } from '@heroicons/react/24/outline';
import { getPortalAppointments, joinPortalTelehealthRoom } from '../../services/portalService';
import Spinner from '../../components/common/Spinner';
import EmptyState from '../../components/common/EmptyState';
import Modal from '../../components/common/Modal';
import TelehealthRoom from '../../components/features/telehealth/TelehealthRoom';
import toast from 'react-hot-toast';

const statusColor = {
  pending_approval: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  scheduled:        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  confirmed:        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  completed:        'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
  cancelled:        'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
  no_show:          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  rescheduled:      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

const statusLabel = {
  pending_approval: 'Pending approval',
  scheduled:        'Scheduled',
  confirmed:        'Confirmed',
  completed:        'Completed',
  cancelled:        'Cancelled',
  no_show:          'No show',
  rescheduled:      'Rescheduled',
};

function isJoinable(appt) {
  const now = new Date();
  const start = new Date(appt.startTime);
  const end = new Date(start.getTime() + (appt.durationMinutes ?? 50) * 60 * 1000);
  const windowStart = new Date(start.getTime() - 15 * 60 * 1000);
  const windowEnd = new Date(end.getTime() + 15 * 60 * 1000);
  return now >= windowStart && now <= windowEnd;
}

function AppointmentRow({ appt, onJoin }) {
  const canJoin = appt.isTelehealth && appt.telehealthLink && isJoinable(appt);

  return (
    <li className="py-4 flex items-start justify-between gap-4">
      <div className="flex items-start gap-4 flex-1 min-w-0">
        {appt.appointmentType?.color && (
          <div
            className="w-1 self-stretch rounded-full shrink-0 mt-1"
            style={{ backgroundColor: appt.appointmentType.color }}
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {appt.appointmentType?.name ?? 'Appointment'}
          </p>
          <p className="text-sm text-neutral-700 dark:text-neutral-300 mt-0.5">
            {format(new Date(appt.startTime), 'EEEE, MMMM d, yyyy')}
          </p>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
            {format(new Date(appt.startTime), 'h:mm a')}
            {appt.durationMinutes && ` · ${appt.durationMinutes} min`}
            {appt.practitioner?.user &&
              ` · ${appt.practitioner.user.firstName} ${appt.practitioner.user.lastName}`}
            {appt.isTelehealth && ' · Telehealth'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {canJoin && (
          <button
            onClick={() => onJoin(appt)}
            className="btn btn-secondary flex items-center gap-1.5 text-xs py-1.5 px-3 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <VideoCameraIcon className="h-3.5 w-3.5" />
            Join session
          </button>
        )}
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor[appt.status] ?? statusColor.completed}`}>
          {statusLabel[appt.status] ?? appt.status.replace(/_/g, ' ')}
        </span>
      </div>
    </li>
  );
}

export default function PortalAppointments() {
  const [telehealthUrl, setTelehealthUrl] = useState(null);
  const [joiningId, setJoiningId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['portal', 'appointments'],
    queryFn: getPortalAppointments,
  });

  const handleJoin = async (appt) => {
    setJoiningId(appt.id);
    try {
      const { url } = await joinPortalTelehealthRoom(appt.id);
      setTelehealthUrl(url);
    } catch {
      if (appt.telehealthLink) {
        setTelehealthUrl(appt.telehealthLink);
      } else {
        toast.error('Failed to get telehealth join link');
      }
    } finally {
      setJoiningId(null);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>;
  }

  const { upcoming = [], past = [] } = data ?? {};

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Appointments</h1>

      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Upcoming</h2>
        {!upcoming.length ? (
          <EmptyState title="No upcoming appointments" description="Your next appointment will appear here once scheduled." />
        ) : (
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {upcoming.map((appt) => (
              <AppointmentRow
                key={appt.id}
                appt={appt}
                onJoin={joiningId === appt.id ? () => {} : handleJoin}
              />
            ))}
          </ul>
        )}
      </div>

      {past.length > 0 && (
        <div className="card">
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Past</h2>
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {past.map((appt) => <AppointmentRow key={appt.id} appt={appt} onJoin={handleJoin} />)}
          </ul>
        </div>
      )}

      <Modal
        isOpen={!!telehealthUrl}
        onClose={() => setTelehealthUrl(null)}
        title="Telehealth Session"
        size="xl"
      >
        <TelehealthRoom url={telehealthUrl} onClose={() => setTelehealthUrl(null)} />
      </Modal>
    </div>
  );
}
